// Web 用户旅程可靠性层：durable session_started + durable daily_active
// 纯 TypeScript 状态机：无 React/DOM 依赖，存储/发送/时钟全部注入，供 node --test 直接执行。
//
// 语义：
// - session_started：先落盘 announced=false 再发送；仅后端确认（HTTP ok 且 envelope success:true）
//   才标记 announced=true；失败保留同 session_id、同 occurred_at（锚定 startedAt）下次活跃重试。
// - daily_active：按 Asia/Shanghai 历法日持久化为按 day 的记录集合；失败旧日不被新日覆盖，
//   重试保留原 day/sessionId/sequence/occurredAt；仅重试 occurredAt 落在后端 7 日可信窗口内的
//   pending，过期丢弃后才为当前真实活跃创建当日记录；当日成功标 announced 留待跨日清理，
//   旧日成功即删除。
// - 会话轮转：30 分钟 hidden/idle（无事件）后下一次活跃开新会话；idle 起点永远是最近一次活动。
// - 严格 single-flight：session/daily 各自的并发调用复用同一个 inflight attempt promise，
//   失败后没有排队任务立即重发，下一次显式 ensure 才开启新 attempt。
// - 晚到成功回包：仅当前记录 identity 完全匹配（session.id / day+occurredAt+sessionId+sequence）
//   才写回，绝不把旧 session/旧日 record 覆盖到新状态上。
// - 每个公共入口只 capture 一次 hooks.now()，day 与 occurredAt 同源计算，避免跨午夜错配。

export const SESSION_IDLE_MS = 30 * 60 * 1000;
/** 后端 journey occurred_at 可信窗口（trusted delayed retry 上限 7 天） */
export const DAILY_RETRY_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;

// Asia/Shanghai 自 1991 年起固定 UTC+8、无夏令时；埋点只覆盖近期日期，
// 固定偏移与浏览器/测试进程时区无关（测试锁定美洲时区也不得影响上海日历日判定）
const SHANGHAI_OFFSET_MS = 8 * 60 * 60 * 1000;

export type JourneySession = {
  id: string;
  startedAt: number;
  lastActivityAt: number;
  sequence: number;
  /** session_started 的稳定幂等 identity；首次创建即持久化，重试不得变化 */
  startedEventId: string;
  /** session_started 首次占用的序号；后续事件推进 sequence 时仍保持不变 */
  startedSequence: number;
  /** session_started 是否已被后端确认持久化；false 时下次活跃用同一会话重试 */
  announced: boolean;
};

export type DailyActiveRecord = {
  /** Asia/Shanghai 历法日 YYYY-MM-DD */
  day: string;
  /** 当天首次活跃的 ISO 时间戳；重试保持不变 */
  occurredAt: string;
  /** 记录创建时的会话 id；重试沿用 */
  sessionId: string;
  /** 创建时占用的会话内序号；重试保持原值 */
  sequence: number;
  /** daily_active 的稳定幂等 identity；从持久 identity 确定，重试不得变化 */
  eventId: string;
  /** 后端确认持久化后置 true；当日保留用于按日去重，跨日清理 */
  announced: boolean;
};

export interface ReliabilityStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface JourneyReliabilityHooks {
  now(): number;
  createId(): string;
  /** awaitable journey 发送：仅 HTTP ok 且 envelope success:true 时 resolve true；绝不 reject */
  send(context: Record<string, unknown>): Promise<boolean>;
  /** auth/user generation 守卫；旧实例失效后不得再进入会读取当前 token 的 send */
  isActive?(): boolean;
  /** session_started 的会话级元信息（route/viewport/locale 等，由 DOM 侧提供） */
  sessionStartedMeta(): Record<string, unknown>;
}

/** 事件快照：会话字段 + 与入口 now 同源的 occurredAt */
export type JourneyEventRef = JourneySession & {
  /** 事件 occurred_at：与本次入口捕获的 now 同源，避免跨午夜错配 */
  occurredAt: string;
};

/** 当前 Asia/Shanghai 历法日（YYYY-MM-DD） */
export function shanghaiDay(nowMs: number): string {
  return new Date(nowMs + SHANGHAI_OFFSET_MS).toISOString().slice(0, 10);
}

/** 距下一个上海自然日 00:00 的毫秒数（供跨日 timer 使用） */
export function msUntilNextShanghaiMidnight(nowMs: number): number {
  const today = shanghaiDay(nowMs);
  const [year, month, day] = today.split('-').map(Number);
  const nextMidnightMs = Date.UTC(year, month - 1, day + 1) - SHANGHAI_OFFSET_MS;
  return Math.max(1000, nextMidnightMs - nowMs);
}

export class JourneyReliability {
  private readonly userId: string;
  private readonly sessionStorage: ReliabilityStorage | null;
  private readonly dailyStorage: ReliabilityStorage | null;
  private readonly hooks: JourneyReliabilityHooks;
  private session: JourneySession | null = null;
  /** 按 day 的记录集合：当日记录（announced 或 pending）+ 可信窗口内的失败旧日 pending */
  private dailyDays: Record<string, DailyActiveRecord> | null = null;
  /** inflight 绑定 sessionId：旧会话 attempt 在途不吞掉已轮转的新会话 */
  private sessionInflight: { sessionId: string; promise: Promise<void> } | null = null;
  /** 最近一次 session attempt 的 id：settle 补发判定用，同一 id 至多自动补一次 */
  private lastSessionAttemptId: string | null = null;
  /** daily single-flight 必须绑定入口上海日，旧日在途不能吞掉新日 */
  private dailyInflight: { day: string; promise: Promise<void> } | null = null;
  private dailyFollowUp: {
    day: string;
    now: number;
    promise: Promise<void>;
    resolve: () => void;
  } | null = null;

  constructor(
    userId: string,
    sessionStorage: ReliabilityStorage | null,
    dailyStorage: ReliabilityStorage | null,
    hooks: JourneyReliabilityHooks,
  ) {
    this.userId = userId;
    this.sessionStorage = sessionStorage;
    this.dailyStorage = dailyStorage;
    this.hooks = hooks;
  }

  private sessionKey(): string {
    return `dobi.userJourneySession.v1:${this.userId}`;
  }

  private dailyKey(): string {
    return `dobi.userJourneyDailyActive.v1:${this.userId}`;
  }

  private isActive(): boolean {
    return this.hooks.isActive?.() !== false;
  }

  private static isFiniteSequence(value: unknown): value is number {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
  }

  private static isCanonicalIso(value: unknown): value is string {
    if (typeof value !== 'string' || !value) return false;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
  }

  private static isNonEmptyId(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0 && value.trim() === value;
  }

  private static isValidEventId(value: unknown): value is string {
    return typeof value === 'string' && /^[A-Za-z0-9._:-]{8,128}$/.test(value);
  }

  private static eventId(prefix: 'session' | 'daily', parts: Array<string | number>): string {
    const identity = parts.join(':');
    let hashA = 0x811c9dc5;
    let hashB = 0x9e3779b9;
    for (let index = 0; index < identity.length; index += 1) {
      const code = identity.charCodeAt(index);
      hashA ^= code;
      hashA = Math.imul(hashA, 0x01000193);
      hashB ^= code + index;
      hashB = Math.imul(hashB, 0x85ebca6b);
    }
    return `journey-${prefix}-${(hashA >>> 0).toString(16).padStart(8, '0')}${(hashB >>> 0).toString(16).padStart(8, '0')}`;
  }

  private readStoredSession(): JourneySession | null {
    try {
      const raw = this.sessionStorage?.getItem(this.sessionKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<JourneySession>;
      if (
        JourneyReliability.isNonEmptyId(parsed.id)
        && typeof parsed.startedAt === 'number'
        && Number.isFinite(parsed.startedAt)
        && typeof parsed.lastActivityAt === 'number'
        && Number.isFinite(parsed.lastActivityAt)
      ) {
        const sequence = JourneyReliability.isFiniteSequence(parsed.sequence) ? parsed.sequence : 0;
        const startedSequence = JourneyReliability.isFiniteSequence(parsed.startedSequence)
          ? parsed.startedSequence
          : (sequence > 0 ? sequence : 1);
        return {
          id: parsed.id,
          startedAt: parsed.startedAt,
          lastActivityAt: parsed.lastActivityAt,
          sequence: Math.max(sequence, startedSequence),
          startedEventId: JourneyReliability.isValidEventId(parsed.startedEventId)
            ? parsed.startedEventId
            : JourneyReliability.eventId('session', [parsed.id, parsed.startedAt]),
          startedSequence,
          announced: parsed.announced === true,
        };
      }
    } catch {
      // 存储禁用/解析失败 → 走新建流程
    }
    return null;
  }

  private persistSession(): void {
    if (!this.session) return;
    try {
      this.sessionStorage?.setItem(this.sessionKey(), JSON.stringify(this.session));
    } catch {
      // 旁路埋点不能影响页面
    }
  }

  private readStoredDailyDays(): Record<string, DailyActiveRecord> {
    const days: Record<string, DailyActiveRecord> = {};
    try {
      const raw = this.dailyStorage?.getItem(this.dailyKey());
      if (!raw) return days;
      const parsed = JSON.parse(raw) as (
        Partial<DailyActiveRecord> & { days?: Record<string, Partial<DailyActiveRecord>> }
      ) | null;
      if (!parsed || typeof parsed !== 'object') return days;

      // 兼容同 v1 key 的旧单-record shape：合法 day/occurredAt/sessionId 迁移，
      // 缺 sequence 安全用 0，保留 announced
      if (!parsed.days || typeof parsed.days !== 'object') {
        if (
          typeof parsed.day === 'string'
          && /^\d{4}-\d{2}-\d{2}$/.test(parsed.day)
          && JourneyReliability.isCanonicalIso(parsed.occurredAt)
          && JourneyReliability.isNonEmptyId(parsed.sessionId)
          && JourneyReliability.isFiniteSequence(parsed.sequence ?? 0)
        ) {
          const sequence = parsed.sequence ?? 0;
          days[parsed.day] = {
            day: parsed.day,
            occurredAt: parsed.occurredAt,
            sessionId: parsed.sessionId,
            sequence,
            eventId: JourneyReliability.isValidEventId(parsed.eventId)
              ? parsed.eventId
              : JourneyReliability.eventId('daily', [parsed.day, parsed.sessionId, sequence]),
            announced: parsed.announced === true,
          };
        }
        return days;
      }

      for (const [day, record] of Object.entries(parsed.days)) {
        if (
          /^\d{4}-\d{2}-\d{2}$/.test(day)
          && record
          && record.day === day
          && JourneyReliability.isCanonicalIso(record.occurredAt)
          && JourneyReliability.isNonEmptyId(record.sessionId)
          && JourneyReliability.isFiniteSequence(record.sequence)
        ) {
          days[day] = {
            day,
            occurredAt: record.occurredAt,
            sessionId: record.sessionId,
            sequence: record.sequence,
            eventId: JourneyReliability.isValidEventId(record.eventId)
              ? record.eventId
              : JourneyReliability.eventId('daily', [day, record.sessionId, record.sequence]),
            announced: record.announced === true,
          };
        }
      }
    } catch {
      // 解析失败视为空集合，走新建流程
    }
    return days;
  }

  private getDailyDays(): Record<string, DailyActiveRecord> {
    if (!this.dailyDays) {
      this.dailyDays = this.readStoredDailyDays();
    }
    return this.dailyDays;
  }

  private persistDailyDays(): void {
    if (!this.dailyDays) return;
    try {
      this.dailyStorage?.setItem(this.dailyKey(), JSON.stringify({ days: this.dailyDays }));
    } catch {
      // 同上
    }
  }

  private createSession(now: number): JourneySession {
    // 先以 announced=false 落盘，发送确认后才翻 true
    const sessionId = this.hooks.createId();
    const session: JourneySession = {
      id: sessionId,
      startedAt: now,
      lastActivityAt: now,
      sequence: 1,
      startedEventId: JourneyReliability.eventId('session', [sessionId, now]),
      startedSequence: 1,
      announced: false,
    };
    this.session = session;
    this.persistSession();
    return session;
  }

  /** 占用会话内序号并推进活跃时间（daily_active 与 journey 事件共用同一序号流） */
  private nextSessionSequence(session: JourneySession, now: number): number {
    session.sequence += 1;
    session.lastActivityAt = now;
    this.persistSession();
    return session.sequence;
  }

  /**
   * 发送 session_started（严格 single-flight）：
   * - 同一 session 的并发调用共享同一 inflight promise；失败后不自我重试，
   *   下一次显式活跃（announceSession 再次进入且无 inflight）才开启新 attempt；
   * - 旧 session 请求在途期间若已轮转，不在此叠加 attempt —— 旧 attempt 收尾清锁后由
   *   settleSessionAttempt 为当前新会话自动补发且只补一次；晚到旧回包不写新状态。
   */
  private announceSession(now: number): Promise<void> {
    const session = this.session;
    if (!session || session.announced) return Promise.resolve();
    const inflight = this.sessionInflight;
    if (inflight) {
      return inflight.promise;
    }
    return this.startSessionAttempt(now);
  }

  private startSessionAttempt(now: number): Promise<void> {
    const session = this.session;
    if (!session || session.announced) return Promise.resolve();
    const run = this.announceSessionInner(session, now);
    this.lastSessionAttemptId = session.id;
    this.sessionInflight = { sessionId: session.id, promise: run };
    const settle = () => this.settleSessionAttempt(run);
    run.then(settle, settle);
    return run;
  }

  private settleSessionAttempt(run: Promise<void>): void {
    if (this.sessionInflight?.promise === run) {
      this.sessionInflight = null;
    }
    // 轮转补发：当前会话与已收尾 attempt 不同 id、unannounced、且尚未为它启动过 attempt。
    // lastSessionAttemptId 保证同一 id 至多自动补一次；同 id 失败不在此自我重试。
    // 补发传 current.lastActivityAt：网络完成时刻不算用户活动，不得推迟 30 分钟 idle 起点
    const current = this.session;
    if (
      current
      && !current.announced
      && this.lastSessionAttemptId !== current.id
    ) {
      void this.startSessionAttempt(current.lastActivityAt);
    }
  }

  private async announceSessionInner(session: JourneySession, now: number): Promise<void> {
    if (
      session.announced
      || !this.isActive()
      || session.startedAt > now
      || now - session.startedAt > DAILY_RETRY_LOOKBACK_MS
    ) return;
    try {
      // event_id/sequence 在 session 首次创建时已经落盘；所有重试沿用同一 identity。
      if (!this.isActive()) return;
      const sent = await this.hooks.send({
        schema_version: 1,
        event_name: 'session_started',
        event_id: session.startedEventId,
        session_id: session.id,
        sequence: session.startedSequence,
        // occurred_at 锚定会话开始时刻：延迟重试仍归入会话开始时刻而非重试时的墙钟
        occurred_at: new Date(session.startedAt).toISOString(),
        ...this.hooks.sessionStartedMeta(),
      });
      if (!sent || !this.isActive()) return;
      // 晚到成功：仅当前会话 identity 完全匹配才写回
      if (this.session && this.session.id === session.id && !this.session.announced) {
        this.session.announced = true;
        this.persistSession();
      }
    } catch {
      // 旁路埋点任何失败都不允许外抛
    }
  }

  private getSessionAt(now: number): JourneySession {
    let session = this.session ?? this.readStoredSession();
    if (
      !session
      || now - session.lastActivityAt >= SESSION_IDLE_MS
      || (!session.announced && (
        session.startedAt > now
        || now - session.startedAt > DAILY_RETRY_LOOKBACK_MS
      ))
    ) {
      session = this.createSession(now);
    }
    this.session = session;
    if (!session.announced) {
      void this.announceSession(now);
    }
    return session;
  }

  /**
   * 当前会话：不存在则读取/新建；30 分钟 hidden/idle（以最近一次活动为起点）后轮转新会话。
   * 未宣告的会话会安排 single-flight 的 session_started 发送。入口单次捕获 now。
   */
  getSession(): JourneySession {
    return this.getSessionAt(this.hooks.now());
  }

  /**
   * 旅程事件出口：轮转检查 + 序号/活跃时间落盘，返回事件快照。
   * 入口单次捕获 now；occurredAt 与该 now 同源，避免跨午夜事件时间与会话判定错配。
   */
  beginEvent(): JourneyEventRef {
    const now = this.hooks.now();
    const session = this.getSessionAt(now);
    const sequence = this.nextSessionSequence(session, now);
    return { ...session, sequence, occurredAt: new Date(now).toISOString() };
  }

  /**
   * 每日活跃去重上报：按 day 持久化的 pending 集合；失败旧日保留原 day/sessionId/
   * sequence/occurredAt 跨日重试（最旧优先），仅 7 日可信窗口内的 pending 会重发；
   * 严格 single-flight——并发调用复用同一 attempt promise，失败后不排队重发。永不 reject。
   */
  private startDailyFlight(now: number): Promise<void> {
    const day = shanghaiDay(now);
    const run = this.ensureDailyActiveInner(now);
    this.dailyInflight = { day, promise: run };
    const clear = () => {
      if (this.dailyInflight?.promise === run) {
        this.dailyInflight = null;
      }
      const followUp = this.dailyFollowUp;
      this.dailyFollowUp = null;
      if (!followUp) return;
      const next = this.startDailyFlight(followUp.now);
      next.then(followUp.resolve, followUp.resolve);
    };
    run.then(clear, clear);
    return run;
  }

  ensureDailyActive(): Promise<void> {
    const now = this.hooks.now(); // 入口单次 capture：day/occurredAt 同源
    const day = shanghaiDay(now);
    const inflight = this.dailyInflight;
    if (!inflight) {
      return this.startDailyFlight(now);
    }
    if (inflight.day === day) {
      return inflight.promise;
    }
    const queued = this.dailyFollowUp;
    if (queued) {
      // 极端长请求跨过多日时只保留最新真实活跃日，但共享同一个 follow-up Promise。
      queued.day = day;
      queued.now = now;
      return queued.promise;
    }
    let resolveFollowUp: () => void = () => undefined;
    const promise = new Promise<void>((resolve) => { resolveFollowUp = resolve; });
    this.dailyFollowUp = { day, now, promise, resolve: resolveFollowUp };
    return promise;
  }

  private async ensureDailyActiveInner(now: number): Promise<void> {
    try {
      if (!this.isActive()) return;
      const today = shanghaiDay(now);
      const days = this.getDailyDays();

      // 清理：已确认的过往日 + 超出可信窗口/时间倒挂的 pending；当日的 malformed/future/
      // 过期 pending 同样删除（随后按本次 now 重建真实当日记录），不能让坏记录永久阻塞
      let cleaned = false;
      for (const [day, record] of Object.entries(days)) {
        if (day === today) {
          if (!this.isWellFormedDailyRecord(record, now)) {
            delete days[day];
            cleaned = true;
          }
          continue;
        }
        if (!this.isRetryablePending(record, now)) {
          delete days[day];
          cleaned = true;
        }
      }
      if (cleaned) {
        this.persistDailyDays();
      }

      // 当日记录不存在：以当前真实活跃创建（occurredAt 与入口 now 同源）
      if (!days[today]) {
        const session = this.getSessionAt(now);
        const sequence = this.nextSessionSequence(session, now);
        days[today] = {
          day: today,
          occurredAt: new Date(now).toISOString(),
          sessionId: session.id,
          sequence,
          eventId: JourneyReliability.eventId('daily', [today, session.id, sequence]),
          announced: false,
        };
        this.persistDailyDays();
      }

      // 发送：按 day 升序，失败旧日优先于当日；每条都用持久化的原值
      const pendings = Object.values(days)
        .filter((record) => !record.announced && this.isRetryablePending(record, now))
        .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
      for (const record of pendings) {
        if (!this.isActive()) return;
        const sent = await this.hooks.send({
          schema_version: 1,
          event_name: 'daily_active',
          event_id: record.eventId,
          session_id: record.sessionId,
          sequence: record.sequence,
          occurred_at: record.occurredAt,
          client_surface: 'web',
        });
        if (!sent) continue; // 保留 pending，下次活跃再试
        if (!this.isActive()) return;
        // 晚到成功：仅当前集合中同 day 记录 identity 完全匹配才写回/清理，
        // 绝不把旧 record 覆盖到跨日后的新状态上
        const store = this.dailyDays;
        if (!store) continue;
        const current = store[record.day];
        if (
          current
          && current.day === record.day
          && current.occurredAt === record.occurredAt
          && current.sessionId === record.sessionId
          && current.sequence === record.sequence
          && current.eventId === record.eventId
          && !current.announced
        ) {
          if (record.day === today) {
            current.announced = true; // 当日保留至跨日清理，按日去重
          } else {
            delete store[record.day]; // 旧日成功后立即清理
          }
          this.persistDailyDays();
        }
      }
    } catch {
      // 同上：任何失败静默
    }
  }

  private isWellFormedDailyRecord(record: DailyActiveRecord, now: number): boolean {
    const occurredMs = Date.parse(record.occurredAt);
    return (
      JourneyReliability.isNonEmptyId(record.sessionId)
      && JourneyReliability.isValidEventId(record.eventId)
      && JourneyReliability.isFiniteSequence(record.sequence)
      && JourneyReliability.isCanonicalIso(record.occurredAt)
      && record.day === shanghaiDay(occurredMs)
      && occurredMs <= now
      && now - occurredMs <= DAILY_RETRY_LOOKBACK_MS
    );
  }

  /** pending 仍在后端可信窗口内：occurredAt 不晚于 now 且不早于 now-7d */
  private isRetryablePending(record: DailyActiveRecord, now: number): boolean {
    return !record.announced && this.isWellFormedDailyRecord(record, now);
  }
}
